import axios from 'axios';
import { API_URL, USE_REAL_API } from './config';

// Создаем экземпляр axios с базовыми настройками
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000, // 15 секунд таймаут
});

// Флаг для предотвращения множественных запросов на обновление токена
let isRefreshing = false;
// Очередь запросов, ожидающих обновления токена
let failedQueue = [];

// Вспомогательные функции для работы с токенами
const getTokenFromStorage = (): string | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem('safonova_access_token');
  } catch (err) {
    console.warn('localStorage unavailable when getting access token', err);
    return null;
  }
};

const getRefreshTokenFromStorage = (): string | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem('safonova_refresh_token');
  } catch (err) {
    console.warn('localStorage unavailable when getting refresh token', err);
    return null;
  }
};

// Обработка запросов в очереди
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Выход пользователя при ошибке авторизации
const logoutOnAuthError = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem('safonova_access_token');
      window.localStorage.removeItem('safonova_refresh_token');
      window.localStorage.removeItem('safonova_user');
    }
  } catch (err) {
    console.warn('localStorage unavailable during logout cleanup', err);
  }

  if (typeof window !== 'undefined' && window.location) {
    try {
      window.location.href = '/auth';
    } catch (err) {
      // ignore navigation errors during SSR or tests
      console.warn('Unable to redirect to /auth', err);
    }
  }
};

// Функция для обновления токена
const refreshTokenRequest = async (): Promise<string | null> => {
  try {
    // 🔵 РАЗРАБОТКА: возвращаем мок-токен при использовании моков
    if (!USE_REAL_API) {
      console.log('[DEV] Мок-обновление токена');
      const mockToken = 'mock-refreshed-token';
      localStorage.setItem('safonova_access_token', mockToken);
      return mockToken;
    }

    // 🟢 ПРОДАКШН: реальное обновление токена через API
    const refreshToken = getRefreshTokenFromStorage();
    if (!refreshToken) {
      return null;
    }
    // Используем чистый axios вместо api для избежливания зацикливания
    // Backend expects { refresh: '<refresh_token>' } and returns { access: '<token>' }
    const response = await axios.post(
      `${API_URL}/users/token/refresh/`,
      { refresh: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const { access } = response.data;

    if (access) {
      localStorage.setItem('safonova_access_token', access);
      return access;
    }

    return null;
  } catch (error) {
    console.error('Ошибка при обновлении токена:', error);
    return null;
  }
};

// Перехватчик запросов для добавления токена авторизации
api.interceptors.request.use(
  (config) => {
    // 🔵 РАЗРАБОТКА: если используются мок-данные, пропускаем добавление токена
    if (!USE_REAL_API) return config;

    const token = getTokenFromStorage();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Перехватчик ответов для обработки ошибок авторизации и обновления токена
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 🔵 РАЗРАБОТКА: если используются мок-данные, просто возвращаем ошибку
    if (!USE_REAL_API) {
      return Promise.reject(error);
    }

    // Если ошибка не связана с авторизацией или запрос уже повторялся
    if (
      !error.response || 
      error.response.status !== 401 || 
      originalRequest._retry
    ) {
      return Promise.reject(error);
    }

    // Если это запрос на обновление токена и он не удался — распознаем по подстроке
    if (originalRequest.url && String(originalRequest.url).includes('/users/token/refresh')) {
      logoutOnAuthError();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // Если уже идет процесс обновления токена
    if (isRefreshing) {
      try {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      } catch (err) {
        return Promise.reject(err);
      }
    }

    isRefreshing = true;

    try {
      const newToken = await refreshTokenRequest();
      
      if (newToken) {
        // Обновляем заголовок для текущего запроса
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        
        // Обрабатываем очередь ожидающих запросов
        processQueue(null, newToken);
        
        return api(originalRequest);
      } else {
        // Если не удалось обновить токен
        processQueue(new Error('Failed to refresh token'), null);
        logoutOnAuthError();
        return Promise.reject(new Error('Failed to refresh token'));
      }
    } catch (refreshError) {
      processQueue(refreshError, null);
      logoutOnAuthError();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Управление состояниями загрузки для различных ресурсов
const loadingStates = {
  categories: false,
  catalog: false,
  product: false,
  newsDetail: false,
  newsList: false,
  relatedNews: false
};

/**
 * Обновление состояния загрузки для конкретного ресурса
 * 
 * @param {string} resource - Название ресурса (categories, catalog, product, etc.)
 * @param {boolean} isLoading - Флаг загрузки (true - загрузка, false - загрузка завершена)
 **/
export const updateLoadingState = (resource: string, isLoading: boolean): void => {
  loadingStates[resource] = isLoading;
  // Можно добавить здесь глобальное уведомление о загрузке или другую логику
  console.log(`Resource ${resource} loading state: ${isLoading}`);
};

/**
 * Получение данных с сервера через API
 * 
 * @param {string} endpoint - API endpoint
 * @returns {Promise<any>} - Результат запроса
 */
export const fetchData = async (endpoint: string): Promise<any> => {
  try {
    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    console.error(`Error fetching from ${endpoint}:`, error);
    throw error;
  }
};

export default api;
export { USE_REAL_API };